import type { Params } from '@vimcar/mvvm/linkViewModel'
import type { ToggleHandler } from '@vimcar/shared-utils/changeHandler'

import { mkToggleHandler } from '@vimcar/shared-utils/changeHandler'
import * as mobx from 'mobx'
import PT from 'prop-types'

import type { CheckboxesNode } from '../../models/Nodes/CheckboxesNode'
import type { ViewState } from '../Root/viewModel'
import type { Transitioner } from '../TransitionProvider'

import { ATTRIBUTE_CHECKBOXES } from '../../utils/constants'
import i18n from '../../utils/i18n'

const tp = i18n.prefixWithoutTestId('CheckboxesNode')

export type Props = {
  state: ViewState
}

export type ViewModel = {
  title: string
  subTitle: string
  options: Array<[string, ToggleHandler]>
  error?: string | null
  showError: boolean
  isValid: boolean
  handleNext: () => void
}

type Context = {
  transitioner: Transitioner
}

const mkViewModel = (
  params: Params,
  props: Props,
  { transitioner }: Context,
) => {
  const currentNode: mobx.IComputedValue<CheckboxesNode> = mobx.computed(
    () => props.state.currentNode,
  )

  const value = mobx.computed(() =>
    vm.options.filter(([_, handler]) => handler.value).map(([label]) => label),
  )

  const showError = mobx.observable.box(false)

  const vm: ViewModel = mobx.observable.shallowObject({
    get title() {
      return currentNode.get().text || ''
    },

    get subTitle() {
      return currentNode.get().subtext || ''
    },

    options: currentNode
      .get()
      .options.map((label) => [label, mkToggleHandler()]),

    get error() {
      const missingRequiredValue =
        currentNode.get().required && !value.get().length

      return missingRequiredValue ? tp('missing-required-value') : undefined
    },

    get showError() {
      return !!(vm.error && showError.get())
    },

    get isValid() {
      return !vm.showError
    },

    handleNext: mobx.action(() => {
      if (vm.error) {
        showError.set(true)
      } else {
        const oldValue =
          props.state.customAttributes.get(ATTRIBUTE_CHECKBOXES) || []
        const newValue = oldValue.concat(value.get())
        props.state.customAttributes.set(ATTRIBUTE_CHECKBOXES, newValue)

        const nextNode = currentNode.get().nextNode
        if (!nextNode) throw new Error('no node linked as next')
        transitioner.goForward(nextNode.id)
      }
    }),
  })

  return vm
}

mkViewModel.contextTypes = {
  transitioner: PT.object.isRequired,
}

export default mkViewModel
